import React, { useEffect, useState, useRef } from 'react';
import './GlobalScene.scss';
import { useLoader, useFrame, useThree } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';
import CameraControls from '../CameraControls/CameraControls';
import gsap from 'gsap';
import { useLocation } from 'react-router-dom';

const GlobalScene = () => {
  const { viewport, camera } = useThree();
  const location = useLocation();
  const camRotationRef = useRef(null);

  // Store initial viewport values to prevent changes on resize
  const [initialViewport, setInitialViewport] = useState(null);

  const [sceneState, setSceneState] = useState({
    rotation: -5,
    position: -0.6,
    z: 30,
  });

  // Capture initial viewport values once
  useEffect(() => {
    if (initialViewport === null) {
      setInitialViewport({
        width: viewport.width,
        height: viewport.height,
        aspect: viewport.aspect,
      });
    }
  }, [viewport, initialViewport]);

  // Use initial viewport if available, otherwise fall back to current viewport
  const currentViewport = initialViewport || viewport;

  const gltf = useLoader(GLTFLoader, '/assets/scene.gltf');
  const mixer = new THREE.AnimationMixer(gltf.scene);
  const clips = gltf.animations;
  const clip = THREE.AnimationClip.findByName(clips, 'Take 001');
  const action = mixer.clipAction(clip);
  action.play();

  // Function to update scene and camera positions/rotations
  const updateSceneAndCamera = config => {
    const {
      targetRotation,
      targetPosition,
      targetCameraZ,
      enableRotation = false,
    } = config;

    // Set initial state
    if (location.pathname === '/') {
      gltf.scene.rotation.y = sceneState.rotation;
      gltf.scene.position.x = sceneState.position;
      camera.position.z = sceneState.z;
    }

    // Disable camera rotation during animation
    if (camRotationRef.current) {
      camRotationRef.current(false);
    }

    // Animate camera
    const cameraTimeline = gsap.timeline({
      onComplete: () => {
        if (enableRotation && camRotationRef.current) {
          camRotationRef.current(true);
        }
      },
    });

    cameraTimeline.to(camera.position, {
      duration: 3,
      ease: 'elastic.out(0.35, 0.3)',
      z: targetCameraZ,
      y: 0,
      x: 0,
    });

    // Animate scene position
    gsap.timeline().to(gltf.scene.position, {
      duration: 1,
      ease: 'power2.out',
      x: targetPosition.x,
      y: targetPosition.y,
      z: targetPosition.z,
    });

    // Animate scene rotation
    gsap.timeline().to(gltf.scene.rotation, {
      duration: 5,
      ease: 'power1.out',
      y: targetRotation.y,
      x: targetRotation.x,
      z: targetRotation.z,
    });

    // Update state
    setSceneState({
      rotation: targetRotation.y,
      position: targetPosition.x,
      z: targetCameraZ,
    });
  };

  // Route configuration mapping
  const helloPosition =
    currentViewport.aspect < 1
      ? { x: -0.015, y: 0.015, z: 0 }
      : { x: -0.05, y: 0, z: 0 };
  const llmoidPosition =
    currentViewport.aspect < 1
      ? { x: 0, y: 0.03, z: 0 }
      : { x: -0.03, y: 0.03, z: 0 };
  const workPosition =
    currentViewport.aspect < 1
      ? { x: 0.02, y: -0.03, z: 0 }
      : { x: 0.05, y: 0, z: 0 };
  const routeConfigs = {
    '/': {
      targetRotation: { y: 1, x: 0, z: 0 },
      targetPosition: { x: 0, y: 0, z: 0 },
      targetCameraZ: 5,
      enableRotation: true,
    },
    '/hello': {
      targetRotation: { y: 8, x: 0, z: 0 },
      targetPosition: helloPosition,
      targetCameraZ: 3,
      enableRotation: false,
    },
    '/llmoid': {
      targetRotation: { y: 4, x: 2, z: 0 },
      targetPosition: llmoidPosition,
      targetCameraZ: 3,
      enableRotation: false,
    },
    '/work': {
      targetRotation: { y: -5, x: 0, z: 0 },
      targetPosition: workPosition,
      targetCameraZ: 4,
      enableRotation: false,
    },
    '/connect': {
      targetRotation: { y: -3, x: 0, z: 0 },
      targetPosition: { x: 0, y: -0.03, z: 0 },
      targetCameraZ: 3,
      enableRotation: false,
    },
  };

  useEffect(() => {
    const config = routeConfigs[location.pathname];
    if (config) {
      updateSceneAndCamera(config);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!currentViewport) return;
    const diagonal = Math.sqrt(
      currentViewport.width * currentViewport.width +
        currentViewport.height * currentViewport.height
    );
    const constant =
      currentViewport.aspect > 1 ? currentViewport.aspect + 0.8 : 2.3;
    gltf.scene.scale.set(
      diagonal / constant,
      diagonal / constant,
      diagonal / constant
    );
  }, [currentViewport, gltf.scene]);

  const clock = new THREE.Clock();
  useFrame(() => mixer.update(clock.getDelta() / 10));

  const getCamRotationControl = cb => {
    camRotationRef.current = cb;
  };

  return (
    <React.Fragment>
      <ambientLight intensity={1.5} />
      <directionalLight intensity={1.5} />
      <primitive object={gltf.scene} />
      <CameraControls getCamRotationControl={getCamRotationControl} />
    </React.Fragment>
  );
};

export default GlobalScene;
